import { normalizeLangKey } from '@src/utility/bcp47';

import { piecesTouchOnClip } from './DubPlacementService';

import type { ClipSpan } from './DubPlacementService';

/**
 * Per-line levels: one dubbed line of a clip played at its own level, the
 * dub's or the original's, instead of the clip's.
 *
 * Pure: no MobX, no React, no IO.
 *
 * A line's level is an OVERRIDE, so it has a different rule from the clip's
 * own: a level left out follows the clip, and full (100) is a real choice that
 * is kept — a line at full under a clip turned down to 60 is the reason this
 * exists. A line with neither level set carries no entry at all, and a clip
 * with no line set carries no key, so the stored shape of every clip that does
 * not use this is unchanged (the publish staleness hash, see
 * `dubOptionsAreEmpty`).
 */

/** One line's own levels; a level left out follows the clip's. */
export interface DubSegmentLevel {
  /** The dubbed voice's level for this line, 0–100. */
  voiceVolume?: number;
  /** The original recording's level for this line on Both, 0–100. */
  originalVolume?: number;
}

/** Manifest `segmentId` → that line's own levels. */
export type DubSegmentLevels = Record<string, DubSegmentLevel>;

/** A stored line level, held to 0–100 and whole; anything else is unset. */
function holdLevel(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  return Math.round(Math.min(100, Math.max(0, raw)));
}

/**
 * The line levels as they are held, or `undefined` when no line has one.
 * Tolerant like the rest of the stored dub options: anything unrecognised is
 * dropped rather than thrown on.
 */
export function holdDubSegmentLevels(
  raw: unknown,
): DubSegmentLevels | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const held: DubSegmentLevels = {};
  for (const [segmentId, value] of Object.entries(raw)) {
    if (segmentId === '' || !value || typeof value !== 'object') continue;
    const voiceVolume = holdLevel(value.voiceVolume);
    const originalVolume = holdLevel(value.originalVolume);
    if (voiceVolume === undefined && originalVolume === undefined) continue;
    held[segmentId] = {
      ...(voiceVolume !== undefined && { voiceVolume }),
      ...(originalVolume !== undefined && { originalVolume }),
    };
  }
  return Object.keys(held).length > 0 ? held : undefined;
}

/** Do two sets of line levels hold the same levels? */
export function sameDubSegmentLevels(
  a: DubSegmentLevels | undefined,
  b: DubSegmentLevels | undefined,
): boolean {
  const heldA = holdDubSegmentLevels(a) ?? {};
  const heldB = holdDubSegmentLevels(b) ?? {};
  const ids = Object.keys(heldA);
  if (ids.length !== Object.keys(heldB).length) return false;
  return ids.every(
    id =>
      heldA[id].voiceVolume === heldB[id]?.voiceVolume &&
      heldA[id].originalVolume === heldB[id]?.originalVolume,
  );
}

/** One line's level set, or cleared with `null`; the rest are kept. */
export function withDubSegmentLevel(
  levels: DubSegmentLevels | undefined,
  segmentId: string,
  level: DubSegmentLevel | null,
): DubSegmentLevels | undefined {
  const next: DubSegmentLevels = { ...(levels ?? {}) };
  if (level) next[segmentId] = level;
  else delete next[segmentId];
  return holdDubSegmentLevels(next);
}

/**
 * What one dubbed language of a clip holds of its own. Line levels name that
 * language's lines, so they stay with it: another language's dub never plays
 * them, and switching back finds them where they were left.
 */
export interface DubLanguageConfiguration {
  segmentLevels?: DubSegmentLevels;
}

/** `normalizeLangKey(language)` → what that language holds of its own. */
export type DubPerLanguageConfiguration = Record<
  string,
  DubLanguageConfiguration
>;

/**
 * The per-language configuration as it is held, or `undefined` when no
 * language holds anything — the same no-empty-key rule as the line levels.
 */
export function holdDubPerLanguageConfiguration(
  raw: unknown,
): DubPerLanguageConfiguration | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const held: DubPerLanguageConfiguration = {};
  for (const [language, value] of Object.entries(raw)) {
    const key = normalizeLangKey(language);
    if (key === '' || !value || typeof value !== 'object') continue;
    const segmentLevels = holdDubSegmentLevels(value.segmentLevels);
    if (segmentLevels) held[key] = { segmentLevels };
  }
  return Object.keys(held).length > 0 ? held : undefined;
}

/** One language's line levels. */
export function segmentLevelsFor(
  configuration: DubPerLanguageConfiguration | undefined,
  language: string | null,
): DubSegmentLevels | undefined {
  if (!language) return undefined;
  return configuration?.[normalizeLangKey(language)]?.segmentLevels;
}

/** One language's line levels replaced, or cleared with `undefined`. */
export function withLanguageSegmentLevels(
  configuration: DubPerLanguageConfiguration | undefined,
  language: string,
  segmentLevels: DubSegmentLevels | undefined,
): DubPerLanguageConfiguration | undefined {
  return holdDubPerLanguageConfiguration({
    ...configuration,
    [normalizeLangKey(language)]: { segmentLevels },
  });
}

/** Do two per-language configurations hold the same? */
export function sameDubPerLanguageConfiguration(
  a: DubPerLanguageConfiguration | undefined,
  b: DubPerLanguageConfiguration | undefined,
): boolean {
  const heldA = holdDubPerLanguageConfiguration(a) ?? {};
  const heldB = holdDubPerLanguageConfiguration(b) ?? {};
  const languages = Object.keys(heldA);
  if (languages.length !== Object.keys(heldB).length) return false;
  return languages.every(language =>
    sameDubSegmentLevels(
      heldA[language].segmentLevels,
      heldB[language]?.segmentLevels,
    ),
  );
}

/** Both levels, as a line or the clip plays them. */
export interface HeardLevels {
  voiceVolume: number;
  originalVolume: number;
}

/** What one line plays at: its own levels, the clip's where it has none. */
export function lineLevels(
  levels: DubSegmentLevels | undefined,
  segmentId: string,
  clip: HeardLevels,
): HeardLevels {
  const own = levels?.[segmentId];
  return {
    voiceVolume: own?.voiceVolume ?? clip.voiceVolume,
    originalVolume: own?.originalVolume ?? clip.originalVolume,
  };
}

/**
 * The override a line needs to play at `heard`: only the levels that differ
 * from the clip's, so a line moved back to the clip's level follows the clip
 * again — `null` when neither differs. Given more than one clip level (the
 * review's and the saved one), a level is kept unless it matches them all.
 */
export function lineOverride(
  heard: HeardLevels,
  ...clips: HeardLevels[]
): DubSegmentLevel | null {
  const level: DubSegmentLevel = {};
  if (clips.some(clip => clip.voiceVolume !== heard.voiceVolume)) {
    level.voiceVolume = heard.voiceVolume;
  }
  if (clips.some(clip => clip.originalVolume !== heard.originalVolume)) {
    level.originalVolume = heard.originalVolume;
  }
  return Object.keys(level).length > 0 ? level : null;
}

/** Where one line's own original level holds, on the clip's clock. */
export interface OriginalLevelRange {
  startSec: number;
  endSec: number;
  /** 0–100. */
  level: number;
}

/** One piece of the original recording's sound, laid as its own element. */
export interface OriginalAudioPiece {
  /** Clip-timeline start, seconds. */
  time: number;
  duration: number;
  /** Offset into the recording, seconds. */
  trim_start: number;
  /** 0–100. */
  level: number;
}

const ms = (sec: number) => Math.round(sec * 1000);

/**
 * The original recording's sound cut into pieces at the edges of the lines
 * that set their own original level, each piece at its own level: the line's
 * where one holds, the clip's everywhere else.
 *
 * The picture element is one element for the whole clip, and a level cannot
 * change part-way along an element (volume keypoints break the render route),
 * so the picture is silenced and its sound laid back as these pieces instead —
 * the music bed's approach, on the same clock: a span's numbers are the
 * pieces' numbers, and a piece starting inside a span skips the same distance
 * into the recording.
 *
 * Neighbouring pieces at the same level inside one span are one piece. Where
 * two ranges overlap, the one that starts first holds.
 */
export function planOriginalAudioPieces(
  spans: readonly ClipSpan[],
  ranges: readonly OriginalLevelRange[],
  clipLevel: number,
): OriginalAudioPiece[] {
  const pieces: OriginalAudioPiece[] = [];
  const sorted = [...ranges].sort((a, b) => a.startSec - b.startSec);

  for (const span of spans) {
    const spanStart = span.clipStartSec;
    const spanEnd = span.clipStartSec + span.durationSec;

    // Every edge inside the span, so each stretch between two edges has one
    // level all the way along.
    const edges = new Set<number>([ms(spanStart), ms(spanEnd)]);
    for (const range of sorted) {
      if (range.startSec > spanStart && range.startSec < spanEnd) {
        edges.add(ms(range.startSec));
      }
      if (range.endSec > spanStart && range.endSec < spanEnd) {
        edges.add(ms(range.endSec));
      }
    }
    const cuts = [...edges].sort((a, b) => a - b);

    for (let i = 0; i < cuts.length - 1; i += 1) {
      const startMs = cuts[i];
      const endMs = cuts[i + 1];
      if (endMs <= startMs) continue;
      const holding = sorted.find(
        range => ms(range.startSec) <= startMs && endMs <= ms(range.endSec),
      );
      const level = holding ? holding.level : clipLevel;
      const time = startMs / 1000;
      const duration = (endMs - startMs) / 1000;

      const last = pieces[pieces.length - 1];
      if (
        last &&
        last.level === level &&
        piecesTouchOnClip(
          { timeSec: last.time, durationSec: last.duration },
          { timeSec: time },
        ) &&
        ms(last.time) >= ms(spanStart)
      ) {
        last.duration = (endMs - ms(last.time)) / 1000;
        continue;
      }
      pieces.push({
        time,
        duration,
        trim_start: span.sourceStartSec + (time - spanStart),
        level,
      });
    }
  }
  return pieces;
}
